import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { INumberStudentStaffRooms } from '../../../shared/models/numberStudentStaffRooms';
import { NumberStudentStaffRoomsService } from '../../../shared/services/numberStudentStaffRooms.service';

@Component({
  selector: 'number-of-student-staff-rooms',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    PrvNextButtonsComponent
  ],
  templateUrl: './number-of-student-staff-rooms.component.html',
  styleUrl: './number-of-student-staff-rooms.component.css'
})
export class NumberOfStudentStaffRoomsComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  // Left-column toggle: the three sub-documents of this page.
  sections = [
    'Number of Students',
    'Number of Staff',
    'Number of Rooms'
  ];
  selectedSection: string = this.sections[0];

  data: INumberStudentStaffRooms = {};

  // Per-sub-document list of labeled fields to render on the right.
  sectionFields: Record<string, { key: keyof INumberStudentStaffRooms; label: string }[]> = {
    'Number of Students': [
      { key: 'students_pre_kindergarten', label: 'Pre-Kindergarten' },
      { key: 'students_kindergarten', label: 'Kindergarten' },
      { key: 'students_grade_1', label: '1st Grade' },
      { key: 'students_grade_2', label: '2nd Grade' },
      { key: 'students_grade_3', label: '3rd Grade' },
      { key: 'students_grade_4', label: '4th Grade' },
      { key: 'students_grade_5', label: '5th Grade' },
      { key: 'students_grade_6', label: '6th Grade' },
      { key: 'students_grade_7', label: '7th Grade' },
      { key: 'students_grade_8', label: '8th Grade' },
      { key: 'students_grade_9', label: '9th Grade' },
      { key: 'students_grade_10', label: '10th Grade' },
      { key: 'students_grade_11', label: '11th Grade' },
      { key: 'students_grade_12', label: '12th Grade' },
      { key: 'students_total', label: 'Total Number of Students in Building:' },
    ],
    'Number of Staff': [
      { key: 'staff_administrators', label: 'Administrators' },
      { key: 'staff_clerical', label: 'Clerical' },
      { key: 'staff_certified', label: 'Certified' },
      { key: 'staff_non_certified', label: 'Non-Certified' },
      { key: 'staff_food_services', label: 'School Food Services' },
      { key: 'staff_maintenance', label: 'Maintenance' },
      { key: 'staff_bus_drivers', label: 'Bus Drivers' },
      { key: 'staff_other', label: 'Other' },
      { key: 'staff_total', label: 'Total Number of Staff in Building:' },
    ],
    'Number of Rooms': [
      { key: 'rooms_interior_classrooms', label: 'Interior Classrooms' },
      { key: 'rooms_portable_classrooms', label: 'Portable Classrooms' },
      { key: 'rooms_miscellaneous', label: 'Miscellaneous Rooms (office, gym, cafeteria, closets)' },
      { key: 'rooms_total', label: 'Total Number of Rooms' },
    ],
  };

  // Per-doc flag: true once the user has manually edited that doc's total,
  // which stops the auto-sum from overwriting their value.
  manualTotals: Record<string, boolean> = {};

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;

  constructor(
    private userDataService: UserDataService,
    private numberStudentStaffRoomsService: NumberStudentStaffRoomsService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadData();
  }

  selectSection(item: string): void {
    this.selectedSection = item;
  }

  isTotalKey(key: string): boolean {
    return key.endsWith('_total');
  }

  getTotalKey(section: string): keyof INumberStudentStaffRooms | null {
    const field = this.sectionFields[section].find(f => this.isTotalKey(f.key as string));
    return field ? field.key : null;
  }

  // Sum the doc's row fields into its total (blank if no rows are filled in).
  recomputeTotal(section: string): void {
    const totalKey = this.getTotalKey(section);
    if (!totalKey) return;

    let sum = 0;
    let hasValue = false;
    for (const field of this.sectionFields[section]) {
      if (this.isTotalKey(field.key as string)) continue;
      const raw = (this.data[field.key] ?? '').toString().trim();
      if (raw === '') continue;
      const parsed = parseFloat(raw);
      if (!isNaN(parsed)) {
        sum += parsed;
        hasValue = true;
      }
    }
    this.data[totalKey] = (hasValue ? String(sum) : '') as any;
  }

  // A row field changed → refresh the auto-sum unless the user overrode the total.
  onRowChange(section: string): void {
    if (!this.manualTotals[section]) {
      this.recomputeTotal(section);
    }
  }

  // The user typed in the total field → stop auto-summing it.
  onTotalEdit(section: string): void {
    this.manualTotals[section] = true;
  }

  loadData(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.numberStudentStaffRoomsService.getNumberStudentStaffRooms(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.data = response.data || {};
            // Populate any blank total from its rows on load.
            this.sections.forEach(section => {
              const totalKey = this.getTotalKey(section);
              const current = totalKey ? this.data[totalKey] : null;
              if (totalKey && (current === undefined || current === null || current === '')) {
                this.recomputeTotal(section);
              }
            });
          }
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      });
    }
  }

  onSubmit(): void {
    if (this.currentBuildingId && this.data) {
      this.loading = true;

      // Only send the fields for the selected sub-document, so saving one
      // doc never clears the other docs' values.
      const allowedKeys = new Set(this.sectionFields[this.selectedSection].map(f => f.key));
      const payload = Object.fromEntries(
        Object.entries(this.data)
          .filter(([key]) => allowedKeys.has(key as keyof INumberStudentStaffRooms))
      );

      this.numberStudentStaffRoomsService.updateNumberStudentStaffRooms(this.currentBuildingId, payload).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after updating Number of Student, Staff, Rooms.';
            this.loadData();
          } else {
            this.messageModal = 'unsuccess';
            this.popupMessage = response.message;
          }
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      });
    }
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }
}
